import React from 'react'
import type { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, Combobox, Input, type ComboboxItem } from '@node-space/ui-components'
import type { ContactEntityType, GovernmentId } from 'types/contacts'
import type { ContactFormData } from '../schemas/contactSchema'
import { getIdTypeForCountryAndEntity, getIdTypesForCountry } from '../utils/governmentIds'

type GovernmentIdCardProps = {
  index: number
  governmentId: GovernmentId
  entityType: ContactEntityType
  countryOptions: ComboboxItem[]
  errors: FieldErrors<ContactFormData>
  onChange: (governmentId: GovernmentId) => void
  onRemove: () => void
}

export const GovernmentIdCard = ({
  index,
  governmentId,
  entityType,
  countryOptions,
  errors,
  onChange,
  onRemove,
}: GovernmentIdCardProps) => {
  const { t } = useTranslation()

  const governmentIdErrors = errors?.governmentIds?.[index]

  const governmentIdTypeOptions: ComboboxItem[] = getIdTypesForCountry(
    governmentId.issuingCountry
  ).map(governmentIdType => ({
    leftText: governmentIdType,
    value: governmentIdType,
  }))

  const handleCountryChange = (value: string) => {
    const resolvedType = getIdTypeForCountryAndEntity(value, entityType)
    onChange({ ...governmentId, issuingCountry: value, type: resolvedType, number: '' })
  }

  return (
    <Card variant="static" full borderRadius={4}>
      <Box flexCol width="full" gapY={16}>
        <Box flex width="full" className="items-center justify-between">
          <span className="text-body-strong-small text-foreground-subdued">
            {t('contacts.governmentIds.cardTitle', { number: index + 1 })}
          </span>
          <Button
            variant="ghost"
            type="button"
            noPadding
            onClick={onRemove}
            testId={`government-id-remove-${index}`}
            aria-label={t('contacts.governmentIds.removeAriaLabel', {
              type: governmentId.type || t('contacts.governmentIds.governmentId'),
            })}
          >
            {t('contacts.governmentIds.remove')}
          </Button>
        </Box>

        <Combobox
          id={`government-id-country-${index}`}
          testId={`government-id-country-${index}`}
          label={t('contacts.governmentIds.issuingCountry')}
          items={countryOptions}
          defaultValue={governmentId.issuingCountry}
          errorText={governmentIdErrors?.issuingCountry?.message}
          onChange={handleCountryChange}
        />
        <Combobox
          key={`government-id-type-${index}-${governmentId.issuingCountry}-${governmentId.type}`}
          id={`government-id-type-${index}`}
          testId={`government-id-type-${index}`}
          label={t('contacts.governmentIds.idType')}
          items={governmentIdTypeOptions}
          disabled
          defaultValue={governmentId.type}
          errorText={governmentIdErrors?.type?.message}
        />
        <Input
          testId={`government-id-number-${index}`}
          label={t('contacts.governmentIds.idNumber')}
          placeholder={t('contacts.governmentIds.idNumber')}
          value={governmentId.number}
          disabled={!governmentId.issuingCountry || !governmentId.type}
          errorText={governmentIdErrors?.number?.message}
          error={!!governmentIdErrors?.number?.message}
          onChange={event => onChange({ ...governmentId, number: event.target.value })}
        />
      </Box>
    </Card>
  )
}
